/**
 * Crafting System Module
 * Pure functions for crafting calculations
 */

import { CRAFTING_CONFIG, getRecipe } from '../../data/crafting';

/**
 * Check if player can afford a recipe
 * @param {Object} recipe - Recipe definition
 * @param {Object} playerResources - Kingdom resources
 * @param {Array} inventory - Player inventory (dungeon materials)
 * @returns {{ canAfford: boolean, missing: Object }}
 */
export function canAffordRecipe(recipe, playerResources = {}, inventory = []) {
  const missing = { resources: {}, materials: {} };
  let canAfford = true;

  // Check kingdom resources
  if (recipe.cost?.resources) {
    for (const [resourceId, amount] of Object.entries(recipe.cost.resources)) {
      const have = playerResources[resourceId] || 0;
      if (have < amount) {
        missing.resources[resourceId] = amount - have;
        canAfford = false;
      }
    }
  }

  // Check dungeon materials (from inventory)
  if (recipe.cost?.materials) {
    for (const [materialId, amount] of Object.entries(recipe.cost.materials)) {
      const have = getInventoryMaterialCount(inventory, materialId);
      if (have < amount) {
        missing.materials[materialId] = amount - have;
        canAfford = false;
      }
    }
  }

  return { canAfford, missing };
}

/**
 * Count material quantity in inventory
 * @param {Array} inventory - Player inventory array
 * @param {string} materialId - Material ID to count
 * @returns {number} Total quantity of material
 */
export function getInventoryMaterialCount(inventory = [], materialId) {
  if (!inventory || !Array.isArray(inventory)) return 0;

  const item = inventory.find(i => i.id === materialId);
  return item ? (item.quantity || 1) : 0;
}

/**
 * Calculate crafting time for a recipe
 * @param {Object} recipe - Recipe definition
 * @param {Object} modifiers - Speed modifiers (from buildings, advisors, etc.)
 * @returns {number} Time in seconds
 */
export function calculateCraftTime(recipe, modifiers = {}) {
  const baseTime = recipe.craftTime || CRAFTING_CONFIG.baseCraftTime[recipe.tier] || 60;

  let speedMultiplier = 1.0;

  // Apply modifiers (e.g., from future crafting speed bonuses)
  if (modifiers.craftingSpeed) {
    speedMultiplier *= (1 + modifiers.craftingSpeed);
  }

  return Math.ceil(baseTime / speedMultiplier);
}

/**
 * Get output quality based on recipe tier
 * @param {Object} recipe - Recipe definition
 * @returns {number} Quality multiplier (0.70 - 1.25)
 */
export function getRecipeQuality(recipe) {
  return CRAFTING_CONFIG.tierQuality[recipe.tier] || 1.0;
}

/**
 * Calculate cancel refund amounts
 * @param {Object} recipe - Recipe definition
 * @returns {Object} { resources: {}, materials: {} }
 */
export function calculateCancelRefund(recipe) {
  const refund = { resources: {}, materials: {} };
  const percent = CRAFTING_CONFIG.cancelRefundPercent;

  if (recipe.cost?.resources) {
    for (const [resourceId, amount] of Object.entries(recipe.cost.resources)) {
      refund.resources[resourceId] = Math.floor(amount * percent);
    }
  }

  if (recipe.cost?.materials) {
    for (const [materialId, amount] of Object.entries(recipe.cost.materials)) {
      refund.materials[materialId] = Math.floor(amount * percent);
    }
  }

  return refund;
}

/**
 * Get max gem slots by equipment slot type
 * @param {string} slot - Equipment slot
 * @returns {number} Max gem slots
 */
export function getMaxGemSlots(slot) {
  const slots = {
    weapon: 7,
    top: 10,
    bottom: 10,
    helmet: 7,
    gloves: 7,
    boots: 7,
    cape: 7,
    belt: 7,
    offhand: 7,
    pendant1: 5,
    ring1: 5,
    ring2: 5,
  };
  return slots[slot] || 7;
}

/**
 * Map tier to rarity for crafted materials
 * @param {number} tier - Recipe tier
 * @returns {string} Rarity string
 */
export function getTierRarity(tier) {
  if (tier <= 1) return 'common';
  if (tier <= 2) return 'uncommon';
  if (tier <= 3) return 'rare';
  if (tier <= 4) return 'epic';
  if (tier <= 5) return 'legendary';
  return 'mythic';
}

/**
 * Format craft time for display
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export function formatCraftTime(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Check if recipe is unlocked (has required tech)
 * @param {Object} recipe - Recipe definition
 * @param {Array} researchedTechs - Array of researched tech IDs
 * @returns {boolean} True if unlocked
 */
export function isRecipeUnlocked(recipe, researchedTechs = []) {
  if (!recipe.requiresTech) return true;
  return researchedTechs.includes(recipe.requiresTech);
}

/**
 * Get all costs as a single object (for display)
 * @param {Object} recipe - Recipe definition
 * @returns {Object} { resources: {}, materials: {} }
 */
export function getRecipeCosts(recipe) {
  return {
    resources: recipe.cost?.resources || {},
    materials: recipe.cost?.materials || {},
  };
}
